import type { KeyboardEvent, MouseEvent } from 'react';
import { forwardRef } from 'react';
import type { SxProps, Theme } from '@mui/material';
// Deep icon imports so bundlers that don't transform the @mui/icons-material
// barrel still resolve each icon.
import PhotoIcon from '@mui/icons-material/Photo';
import CloseIcon from '@mui/icons-material/Close';
import ErrorIcon from '@mui/icons-material/Error';
// Loads the CommonColors module augmentation (lightGray, darkGray, ...).
import '../../Theme/palette';
import { Box } from '../../Layout/Box';
import { Typography } from '../../Typography/Typography';
import { CircularProgress } from '../../Feedback/CircularProgress';
import { IconButton } from '../../Buttons/IconButton';

export type PhotoChipVariant = 'removable' | 'nonRemovable';

export type PhotoChipStatus = 'uploading' | 'error';

interface PhotoChipBaseProps {
  /**
   * Full image file name. The component truncates it for display (8 characters
   * for the removable variant, 11 for nonRemovable) and exposes the full name
   * via the `title` attribute.
   */
  imageName: string;
  /**
   * Whole-chip tap handler (e.g. open a photo preview). When set, the chip
   * gets button semantics: role="button", tabIndex, Enter/Space activation.
   */
  onClick?: () => void;
  /**
   * Upload status. Swaps the leading photo icon for a spinner (`uploading`)
   * or an error icon (`error`). Omit for the idle state.
   */
  status?: PhotoChipStatus;
  id?: string;
  'data-testid'?: string;
  /**
   * System prop to customize the component's styles.
   * Will be merged with default styles.
   */
  sx?: SxProps<Theme>;
}

export type PhotoChipProps =
  | (PhotoChipBaseProps & {
      /** Renders a trailing close control that fires `onRemove`. */
      variant: 'removable';
      /** Fires when the close control is activated. */
      onRemove: () => void;
      /** Already-translated accessible name for the close control. */
      removeLabel: string;
      /** Temporarily hides the close control (e.g. while the parent form is submitting). */
      disableRemove?: boolean;
    })
  | (PhotoChipBaseProps & {
      /** Renders without a remove control. */
      variant: 'nonRemovable';
      onRemove?: never;
      removeLabel?: never;
      disableRemove?: never;
    });

// The removable variant reserves horizontal space for the close icon, hence
// the shorter limit.
const TRUNCATE_LIMITS: Record<PhotoChipVariant, number> = {
  removable: 8,
  nonRemovable: 11,
};

const truncateImageName = (imageName: string, variant: PhotoChipVariant): string => {
  const limit = TRUNCATE_LIMITS[variant];
  return imageName.length > limit ? `${imageName.slice(0, limit)}…` : imageName;
};

const defaultStyles: SxProps<Theme> = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 0.5,
  p: 0.75,
  overflow: 'hidden',
  whiteSpace: 'nowrap',
  backgroundColor: 'common.accentGray',
  // String value prevents MUI sx from multiplying by theme.shape.borderRadius (which can be 0).
  borderRadius: '5px',
};

/**
 * PhotoChip – A single-line chip representing an uploaded photo, shared by the
 * "Create Work" cards (removable) and "Add Photos" / work-card photo lists
 * (nonRemovable).
 *
 * @example
 * ```tsx
 * <PhotoChip
 *   variant="removable"
 *   imageName="crown.jpg"
 *   onRemove={() => removePhoto(photo.photoId)}
 *   removeLabel={translate('work.create.remove_photo', { name: 'crown.jpg' })}
 * />
 * ```
 *
 * @example
 * ```tsx
 * <PhotoChip
 *   variant="nonRemovable"
 *   imageName="prune-before.jpg"
 *   status={photo.status === 'success' ? undefined : photo.status}
 *   onClick={() => openPhoto(photo)}
 * />
 * ```
 */
export const PhotoChip = forwardRef<HTMLDivElement, PhotoChipProps>(
  (
    {
      variant,
      imageName,
      onClick,
      status,
      id,
      'data-testid': dataTestId,
      sx,
      onRemove,
      removeLabel,
      disableRemove,
    },
    ref
  ) => {
    const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        onClick?.();
      }
    };

    const handleRemove = (event: MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation();
      onRemove?.();
    };

    const buttonSemantics = onClick
      ? { role: 'button', tabIndex: 0, onClick, onKeyDown: handleKeyDown }
      : {};

    return (
      <Box
        ref={ref}
        id={id}
        data-testid={dataTestId}
        title={imageName}
        {...buttonSemantics}
        sx={[
          defaultStyles,
          onClick ? { cursor: 'pointer' } : {},
          ...(Array.isArray(sx) ? sx : [sx]),
        ]}
      >
        {status === 'uploading' ? (
          <CircularProgress size={14} thickness={5} />
        ) : status === 'error' ? (
          <ErrorIcon color="error" fontSize="small" />
        ) : (
          <PhotoIcon fontSize="small" sx={{ color: 'common.darkGray' }} />
        )}
        <Typography variant="body1" component="span" color="common.darkGray">
          {truncateImageName(imageName, variant)}
        </Typography>
        {variant === 'removable' && !disableRemove && (
          <IconButton size="small" aria-label={removeLabel} onClick={handleRemove} sx={{ p: 0 }}>
            <CloseIcon fontSize="small" sx={{ color: 'common.lightGray' }} />
          </IconButton>
        )}
      </Box>
    );
  }
);

PhotoChip.displayName = 'PhotoChip';
